/**
 * AssistantWidget — the real home of the AI assistant (djin-009 slice 1).
 *
 * A floating launcher (bottom-right, every page) + a docked slide-over chat panel.
 * Mounted once in AppLayout so it's available app-wide and keeps its conversation across
 * open/close. Talks to POST /api/v1/assistant/chat via the shared `api` instance (djin-006).
 *
 * Slice 1 = the surface: launcher, panel, markdown answers, provenance chips from the raw
 * toolCalls, suggested prompts, thinking/empty/error/offline states, a11y, Esc-to-close.
 * Streaming and typed page context have since landed; djin-007 formalizes the provenance chips.
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, X, Wrench, MapPin, Star } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { config } from '../../config/runtime';
import { useAuthStore } from '../../store/authStore';
import { useAssistantStore, type AssistantChartBlock } from '../../store/assistantStore';
import { ChartBlockView } from './ChartBlockView';
import { useAssistantContext } from './useAssistantContext';
import { AssistantRefusedError, notEnabledMessage, rateLimitedMessage } from './assistantErrors';

const SUGGESTIONS = [
  'What sites do I have?',
  'Any critical alarms?',
  'How is my fleet doing?',
  'Compare two sites',
];

// Markdown gets rendered into the app theme via arbitrary child selectors (no prose plugin).
const MD =
  'text-sm leading-relaxed ' +
  '[&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 ' +
  '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 ' +
  '[&_strong]:font-semibold [&_strong]:text-ems-text-primary ' +
  '[&_a]:text-ems-blue [&_a]:underline ' +
  '[&_code]:rounded [&_code]:bg-ems-bg [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs ' +
  '[&_table]:my-2 [&_table]:w-full [&_table]:text-xs [&_table]:border-collapse ' +
  '[&_th]:border-b [&_th]:border-ems-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold ' +
  '[&_td]:border-b [&_td]:border-ems-border/50 [&_td]:px-2 [&_td]:py-1';

let counter = 0;
const nextId = () => `t${++counter}`;

export function AssistantWidget() {
  const {
    isOpen, turns, loading, favorites, open, close, toggle, addTurn, setLoading, reset,
    startAssistant, appendText, addToolName, addChart, finalizeAssistant, removeTurn,
    toggleFavorite, removeFavorite, clearFavorites, setConversationId,
  } = useAssistantStore();
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const libraryRef = useRef<HTMLDivElement>(null);

  // Close the mid-chat library popover on outside click, and whenever the panel closes.
  useEffect(() => {
    if (!libraryOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!libraryRef.current?.contains(e.target as Node)) setLibraryOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [libraryOpen]);
  useEffect(() => {
    if (!isOpen) setLibraryOpen(false);
  }, [isOpen]);

  // Typed page context sent with every message (never string-injected into the text).
  const { context, siteName } = useAssistantContext();

  // ⌘K / Ctrl-K toggles the panel from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  // Auto-scroll to the newest message.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, loading]);

  // Focus the composer on open; Esc closes.
  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  const send = async (text?: string, retryingAfter404 = false): Promise<void> => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setError(null);
    if (!retryingAfter404) addTurn({ id: nextId(), role: 'user', text: message }); // show the raw message
    setInput('');
    setLoading(true);

    // Stream the answer via SSE so it types out live (djin-009 slice 2). A native fetch is
    // used (not the axios `api`) because we need to read the response body as a stream.
    const assistantId = nextId();
    startAssistant(assistantId);
    let streamedAnything = false;
    let textStreamed = false;
    try {
      const token = useAuthStore.getState().token;
      const body: Record<string, unknown> = { message, ...(context ? { context } : {}) };
      const activeConversation = useAssistantStore.getState().conversationId;
      if (activeConversation) body.conversationId = activeConversation;
      const res = await fetch(`${config.API_URL}/v1/assistant/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (res.status === 404 && activeConversation && !retryingAfter404) {
        // The server no longer knows this conversation (restart, rollback, expiry): start over once.
        setConversationId(null);
        removeTurn(assistantId);
        setLoading(false);
        return await send(message, true);
      }
      if (res.status === 409) {
        setConversationId(null);
        throw new AssistantRefusedError('This conversation is full — start a new one.');
      }
      if (res.status === 503) throw new Error('offline');
      // Per-user / per-tenant limit (#859): say which, and how long to wait.
      if (res.status === 429) throw new AssistantRefusedError(await rateLimitedMessage(res));
      // Tenant switch turned off after this page loaded (#860).
      if (res.status === 403) throw new AssistantRefusedError(await notEnabledMessage(res));
      // This is a raw fetch, so it bypasses the axios response interceptor in
      // services/api.ts that clears auth and redirects on a 401. Without this the
      // rest of the app force-logs-out on an expired JWT while the assistant shows
      // a generic error and strands the user on a dead session.
      if (res.status === 401) {
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        throw new Error('unauthorized');
      }
      if (!res.ok || !res.body) throw new Error('failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          const line = frame.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          let ev: { type: string; delta?: string; name?: string; result?: { answer: string; conversationId?: string; capped?: boolean; toolCalls?: { name: string }[] }; message?: string; chart?: AssistantChartBlock };
          try {
            ev = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (ev.type === 'text' && ev.delta) {
            streamedAnything = true;
            textStreamed = true;
            appendText(assistantId, ev.delta);
          } else if (ev.type === 'tool' && ev.name) {
            addToolName(assistantId, ev.name);
          } else if (ev.type === 'chart' && ev.chart) {
            // done.result.charts is ignored: charts arrive as their own events.
            addChart(assistantId, ev.chart);
          } else if (ev.type === 'done' && ev.result) {
            streamedAnything = true;
            if (ev.result.conversationId) setConversationId(ev.result.conversationId);
            // A run that exhausts maxIterations while still calling tools (`capped`)
            // finishes with an empty answer and may never have streamed a text delta.
            // finalizeAssistant keeps the existing text when the new one is empty, and
            // an empty bubble renders as the animated "thinking" dots — `loading` is
            // already false by then, so nothing would ever clear them. Substitute a
            // real message so the turn always resolves to something readable.
            const answer =
              ev.result.answer ||
              (textStreamed
                ? '' // keep what already streamed
                : ev.result.capped
                  ? 'I ran out of steps before I could finish that. Try narrowing the question.'
                  : "I couldn't produce an answer for that. Try rephrasing it.");
            finalizeAssistant(assistantId, answer, ev.result.toolCalls);
          } else if (ev.type === 'error') {
            throw new Error(ev.message || 'failed');
          }
        }
      }
    } catch (e: unknown) {
      if (!streamedAnything) removeTurn(assistantId); // drop the empty bubble
      const offline = e instanceof Error && e.message === 'offline';
      setError(
        e instanceof AssistantRefusedError
          ? e.userMessage
          : offline
            ? 'The assistant is offline right now. Please try again later.'
            : 'Something went wrong. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            key="assistant-launcher"
            initial={{ opacity: 0, scale: 0.8, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 22 }}
            onClick={open}
            className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-gradient-to-br from-ems-blue to-ems-cyan px-4 py-3 text-sm font-semibold text-ems-bg shadow-lg shadow-ems-blue/25 hover:brightness-110"
            aria-label="Open the DJIN AI assistant"
          >
            <Sparkles size={18} />
            <span className="hidden sm:inline">Ask AI</span>
            <kbd className="hidden rounded bg-white/25 px-1 text-[10px] font-semibold leading-4 sm:inline">⌘K</kbd>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel (+ mobile backdrop) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="assistant-backdrop"
            className="fixed inset-0 z-40 bg-black/50 sm:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />
        )}
        {isOpen && (
          <motion.aside
            key="assistant-panel"
            initial={{ opacity: 0, x: 48 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 48 }}
            transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[440px] flex-col border-l border-ems-border bg-ems-bg-card shadow-2xl sm:inset-y-4 sm:right-4 sm:rounded-2xl sm:border"
            role="dialog"
            aria-label="DJIN AI Assistant"
          >
            {/* Header */}
            <header className="flex items-center gap-3 border-b border-ems-border px-4 py-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-ems-blue/15 text-ems-blue">
                <Sparkles size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-ems-text-primary">DJIN Assistant</div>
                <div className="flex items-center gap-1.5 text-[11px] text-ems-text-muted">
                  <span className="h-1.5 w-1.5 rounded-full bg-ems-green" />
                  Read-only · AI-generated
                </div>
              </div>
              {turns.length > 0 && (
                <button
                  onClick={() => {
                    reset();
                    setError(null);
                  }}
                  disabled={loading}
                  className="rounded-full border border-ems-border px-2.5 py-1 text-[11px] font-medium text-ems-text-muted hover:border-ems-blue hover:text-ems-text-primary disabled:opacity-40"
                >
                  New conversation
                </button>
              )}
              <button
                onClick={close}
                className="rounded-md p-1.5 text-ems-text-muted hover:bg-ems-bg-card-hover hover:text-ems-text-primary"
                aria-label="Close assistant"
              >
                <X size={18} />
              </button>
            </header>

            {/* Transcript */}
            <div
              ref={scrollRef}
              role="log"
              aria-live="polite"
              aria-relevant="additions text"
              className="flex-1 space-y-4 overflow-y-auto px-4 py-4"
            >
              {turns.length === 0 && (
                <div className="pt-8">
                  <div className="text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-ems-blue/10 text-ems-blue">
                      <Sparkles size={22} />
                    </div>
                    <p className="text-sm text-ems-text-secondary">Ask about your sites, power, or alarms.</p>
                  </div>

                  {favorites.length > 0 && (
                    <div className="mt-5">
                      <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-ems-text-muted">
                        <span className="flex items-center gap-1.5">
                          <Star size={12} className="fill-ems-amber text-ems-amber" /> Saved prompts
                        </span>
                        <button
                          onClick={clearFavorites}
                          className="font-medium normal-case tracking-normal text-ems-text-muted hover:text-ems-red"
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {favorites.map((f) => (
                          <span
                            key={f}
                            className="inline-flex items-center gap-1.5 rounded-full border border-ems-amber/30 bg-ems-amber/10 py-1.5 pl-3 pr-2 text-xs text-ems-text-secondary"
                          >
                            <button
                              onClick={() => send(f)}
                              className="max-w-[220px] truncate text-left hover:text-ems-text-primary"
                              title={f}
                            >
                              {f}
                            </button>
                            <button
                              onClick={() => removeFavorite(f)}
                              className="text-ems-text-muted opacity-60 hover:text-ems-red hover:opacity-100"
                              aria-label="Remove saved prompt"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-5">
                    {favorites.length > 0 && (
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ems-text-muted">
                        Try asking
                      </div>
                    )}
                    <div className={`flex flex-wrap gap-2 ${favorites.length > 0 ? '' : 'justify-center'}`}>
                      {SUGGESTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => send(s)}
                          className="rounded-full border border-ems-border bg-ems-bg-card-hover px-3 py-1.5 text-xs text-ems-text-secondary hover:border-ems-blue hover:text-ems-text-primary"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {turns.map((t) => {
                const tools = t.toolCalls ? [...new Set(t.toolCalls.map((c) => c.name))] : [];
                const saved = t.role === 'user' && favorites.includes(t.text);
                return (
                  <div
                    key={t.id}
                    className={`group flex items-center gap-1.5 ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {t.role === 'user' && (
                      <button
                        onClick={() => toggleFavorite(t.text)}
                        className="shrink-0 text-ems-amber hover:text-ems-amber/80"
                        aria-label={saved ? 'Remove from saved prompts' : 'Save this prompt'}
                        title={saved ? 'Saved — click to remove' : 'Save prompt'}
                      >
                        <Star size={14} className={saved ? 'fill-ems-amber' : ''} />
                      </button>
                    )}
                    <div
                      className={
                        t.role === 'user'
                          ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-ems-blue px-3.5 py-2 text-sm text-ems-bg'
                          : 'max-w-[92%] rounded-2xl rounded-bl-sm bg-ems-bg-card-hover px-3.5 py-2.5 text-ems-text-primary'
                      }
                    >
                      {t.role === 'user' ? (
                        <span className="whitespace-pre-wrap">{t.text}</span>
                      ) : t.text === '' ? (
                        <span className="flex items-center gap-2 text-sm text-ems-text-secondary">
                          <span className="flex gap-1">
                            {[0, 0.2, 0.4].map((d) => (
                              <motion.span
                                key={d}
                                className="h-1.5 w-1.5 rounded-full bg-ems-blue"
                                animate={{ opacity: [0.3, 1, 0.3] }}
                                transition={{ duration: 1, repeat: Infinity, delay: d }}
                              />
                            ))}
                          </span>
                          consulting your data…
                        </span>
                      ) : (
                        <div className={MD}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.text}</ReactMarkdown>
                        </div>
                      )}
                      {t.charts?.map((c) => <ChartBlockView key={c.id} chart={c} />)}
                      {tools.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {tools.map((name) => (
                            <span
                              key={name}
                              className="inline-flex items-center gap-1 rounded-full bg-ems-cyan/10 px-2 py-0.5 text-[11px] font-medium text-ems-cyan"
                            >
                              <Wrench size={11} /> {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                className="mx-4 mb-2 rounded-lg border border-ems-red/40 bg-ems-red/10 px-3 py-2 text-xs text-ems-red"
              >
                {error}
              </div>
            )}

            {/* Current-site context chip */}
            {siteName && (
              <div className="mx-4 mb-1.5 flex items-center gap-1.5 text-[11px] text-ems-text-muted">
                <MapPin size={12} className="text-ems-blue" />
                Viewing <span className="font-medium text-ems-text-secondary">{siteName}</span>
              </div>
            )}

            {/* Composer */}
            <div className="border-t border-ems-border p-3">
              <div className="flex items-end gap-2 rounded-xl border border-ems-border bg-ems-bg px-3 py-2 focus-within:border-ems-blue">
                {favorites.length > 0 && (
                  <div ref={libraryRef} className="relative">
                    <button
                      onClick={() => setLibraryOpen((o) => !o)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-ems-amber hover:bg-ems-bg-card-hover"
                      aria-label="Saved prompts"
                      title="Saved prompts"
                    >
                      <Star size={16} className="fill-ems-amber" />
                    </button>
                    <AnimatePresence>
                      {libraryOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 6 }}
                          transition={{ duration: 0.12 }}
                          className="absolute bottom-full left-0 z-20 mb-2 max-h-64 w-64 overflow-y-auto rounded-xl border border-ems-border bg-ems-bg-card p-1.5 shadow-2xl"
                        >
                          <div className="flex items-center justify-between px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-ems-text-muted">
                            <span>Saved prompts</span>
                            <button
                              onClick={() => {
                                clearFavorites();
                                setLibraryOpen(false);
                              }}
                              className="font-medium tracking-normal hover:text-ems-red"
                            >
                              Clear all
                            </button>
                          </div>
                          {favorites.map((f) => (
                            <button
                              key={f}
                              onClick={() => {
                                setLibraryOpen(false);
                                send(f);
                              }}
                              className="block w-full truncate rounded-lg px-2 py-1.5 text-left text-xs text-ems-text-secondary hover:bg-ems-bg-card-hover hover:text-ems-text-primary"
                              title={f}
                            >
                              {f}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder="Ask about your sites, power, or alarms…"
                  className="max-h-28 flex-1 resize-none bg-transparent text-sm text-ems-text-primary placeholder:text-ems-text-muted focus:outline-none"
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ems-blue text-ems-bg disabled:opacity-40"
                  aria-label="Send message"
                >
                  <Send size={16} />
                </button>
              </div>
              <p className="mt-1.5 px-1 text-[10px] text-ems-text-muted">
                Read-only · only sees your own sites' data.
              </p>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
